// src/catalog/components/detail/catalogDetailProfiles.js

import {
  formatLabel,
  formatResourceFormat,
  humanizeValue
} from "../../utils/catalogDisplay";

function getTagValue(tags = [], namespace) {
  const prefix = `${namespace}:`;
  const tag = tags.find((value) => value.startsWith(prefix));

  return tag ? tag.slice(prefix.length) : null;
}

function getResourcePublisher(entry) {
  const primarySource = entry?.rights?.sources?.[0];
  const explicitPublisher =
    entry?.publisher ||
    entry?.metadata?.publisher ||
    primarySource?.publisher;

  if (explicitPublisher) {
    return explicitPublisher;
  }

  if (
    entry?.ownershipModel === "allVoicedOwned" ||
    primarySource?.sourceType === "allVoicedOriginal"
  ) {
    return primarySource?.rightsHolder || "All-Voiced";
  }

  return null;
}

function getResourceMetadataRows(entry) {
  const subtype = getTagValue(entry?.tags, "resource_subtype");
  const format = getTagValue(entry?.tags, "format");
  const publisher = getResourcePublisher(entry);

  return [
    subtype
      ? {
          label: "Resource subtype",
          value: humanizeValue(subtype)
        }
      : null,
    format
      ? {
          label: "Format",
          value: formatResourceFormat(format)
        }
      : null,
    publisher
      ? {
          label: "Publisher",
          value: publisher
        }
      : null
  ].filter(Boolean);
}

const DEFAULT_PROFILE = Object.freeze({
  heroMetaFields: [
    "primaryType",
    "deliveryMode",
    "lifecycleStatus",
    "participationMode"
  ],
  panels: Object.freeze({
    purpose: true,
    access: true,
    details: true,
    metadata: null,
    instrument: true,
    rights: true,
    relatedEntries: true,
    contact: true,
    links: true,
    tags: false
  })
});

const RESOURCE_PROFILE = Object.freeze({
  heroMetaFields: [
    "primaryType",
    "lifecycleStatus"
  ],
  panels: Object.freeze({
    purpose: true,
    access: true,
    details: false,
    metadata: "resource",
    instrument: false,
    rights: true,
    relatedEntries: false,
    contact: true,
    links: true,
    tags: false
  })
});

export function getCatalogEntryDetailProfile(entry) {
  if (entry?.primaryType === "resource") {
    return RESOURCE_PROFILE;
  }

  return DEFAULT_PROFILE;
}

export function buildCatalogEntryHeroMetaItems(entry, profile) {
  const fields = profile?.heroMetaFields || DEFAULT_PROFILE.heroMetaFields;

  return fields
    .map((field) => {
      if (field === "primaryType") {
        return entry?.primaryType ? formatLabel(entry.primaryType) : null;
      }

      if (field === "deliveryMode") {
        return entry?.deliveryMode ? formatLabel(entry.deliveryMode) : null;
      }

      if (field === "lifecycleStatus") {
        return entry?.lifecycleStatus ? formatLabel(entry.lifecycleStatus) : null;
      }

      if (field === "participationMode") {
        return entry?.participationMode ? formatLabel(entry.participationMode) : null;
      }

      return null;
    })
    .filter(Boolean);
}

export function buildCatalogEntryMetadataPanelProps(entry, profile) {
  if (profile?.panels?.metadata !== "resource") {
    return null;
  }

  const rows = getResourceMetadataRows(entry);

  if (rows.length === 0) {
    return null;
  }

  return {
    title: "Resource details",
    rows
  };
}
